import { useNavigate } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { ButtonLarge, ButtonLink } from "../../shared/uikit";

/*
 * TODO: Replace mock data with real API data once the endpoint is available.
 * Requires GET /api/user/instance endpoint to fetch actual license info.
 * Currently using mock data for UI development and testing.
 */

interface LicenseInfo {
  type: string;
  number: string;
  validFrom: string;
  validTo: string;
  status: string;
  usage: {
    users: { current: number; max: number };
    groups: { current: number; max: number };
    devices: { current: number; max: number };
  };
}

const MOCK_LICENSE: LicenseInfo = {
  type: "Individual",
  number: "IND-001-A1B2C3D4",
  validFrom: "2025-01-01T00:00:00Z",
  validTo: "2025-12-31T23:59:59Z",
  status: "Active",
  usage: {
    users: { current: 3, max: 5 },
    groups: { current: 2, max: 10 },
    devices: { current: 4, max: 8 },
  },
};

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export const LicensePage = () => {
  const navigate = useNavigate();
  const license = MOCK_LICENSE;

  const renderUsageBar = (
    current: number,
    max: number,
    label: string,
    icon: string,
  ) => {
    const percent = max > 0 ? Math.min((current / max) * 100, 100) : 0;
    const barColor =
      percent >= 90
        ? "var(--accent-color)"
        : percent >= 70
          ? "#f0a030"
          : "#28a745";

    return (
      <div style={{ marginTop: "0.75rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "14px",
            marginBottom: "0.25rem",
          }}
        >
          <span>
            {icon} {label}
          </span>
          <span style={{ color: "var(--second-color)" }}>
            {current} / {max}
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: "10px",
            background: "var(--second-background)",
            borderRadius: "5px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: "100%",
              background: barColor,
              borderRadius: "5px",
              transition: "width 0.3s ease",
            }}
          />
        </div>
        <span style={{ fontSize: "12px", color: "var(--second-color)" }}>
          {Math.round(percent)}% used
        </span>
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h2>License Info</h2>

      {/* License type badge */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1.5rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          background: "var(--color-bg-secondary)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          <span style={{ fontSize: "32px" }}>📄</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0 }}>{license.type} License</h3>
            <span style={{ fontSize: "13px", color: "var(--second-color)" }}>
              {license.number}
            </span>
          </div>
          <span
            style={{
              display: "inline-block",
              padding: "4px 14px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              background: "var(--color-success)",
              color: "var(--first-color-inv)",
            }}
          >
            🟢 {license.status}
          </span>
        </div>

        {/* Validity period */}
        <table style={{ width: "100%", fontSize: "14px" }}>
          <tbody>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Valid From
              </td>
              <td style={{ padding: "0.5rem" }}>
                {formatDate(license.validFrom)}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Valid Until
              </td>
              <td style={{ padding: "0.5rem" }}>
                {formatDate(license.validTo)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Usage meters */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem 1.25rem",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)",
          background: "var(--first-background)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem" }}>📊 Usage</h3>

        {renderUsageBar(
          license.usage.users.current,
          license.usage.users.max,
          "Users",
          "👤",
        )}
        {renderUsageBar(
          license.usage.groups.current,
          license.usage.groups.max,
          "Groups",
          "👥",
        )}
        {renderUsageBar(
          license.usage.devices.current,
          license.usage.devices.max,
          "Devices",
          "📱",
        )}
      </div>

      {/* TODO note */}
      <div
        style={{
          marginTop: "1rem",
          padding: "0.75rem 1rem",
          border: "1px dashed var(--color-border)",
          borderRadius: "var(--radius-md)",
          background: "var(--color-bg)",
          fontSize: "12px",
          color: "var(--second-color)",
        }}
      >
        <p style={{ margin: 0 }}>
          📝 <strong>TODO:</strong> Replace mock data with real API data once
          <code
            style={{
              fontSize: "12px",
              background: "var(--color-bg-secondary)",
              padding: "1px 4px",
              borderRadius: "3px",
            }}
          >
            GET /api/user/instance
          </code>
          endpoint is available.
        </p>
      </div>

      {/* Upgrade button */}
      <ButtonLarge
        title="⬆ Upgrade License"
        onClick={() => navigate(getPath(ConfigRoutes.LICENSE_UPGRADE))}
        mode="black"
        Accent={true}
      />

      {/* Navigation */}
      <ButtonLink
        title="← Back to Devices"
        onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
      />
    </div>
  );
};
